import React, { useState } from "react";
import { Props_SnippetVideoCard } from "./VideoCard.props";
import SharedHeading from "../Shared/Heading/Heading";
import SnippetLink from "../Shared/Link/Link";
import { CompileMarkdown } from "../../../../Shared/AttraxTs/React/ReactSnippetMDCompiler";

const getYouTubeVideoId = (url: string): string | null => {
  const match = url.match(
    /(?:youtu\.be\/|youtube\.com\/(?:embed\/|shorts\/|watch\?v=))([0-9A-Za-z_-]{11})/
  );
  return match ? match[1] : null;
};

const getYouTubeThumbnail = (url: string): string | null => {
  const videoId = getYouTubeVideoId(url);
  return videoId ? `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg` : null;
};

const processVideoUrl = (url: string): string => {
  let newUrl = url;
  if (url.includes("watch?v=")) {
    newUrl = url.replace("watch?v=", "embed/");
  } else if (url.includes("youtu.be")) {
    newUrl = url.replace("youtu.be/", "youtube.com/embed/");
  } else if (url.includes("youtube.com/shorts/")) {
    newUrl = url.replace("youtube.com/shorts/", "youtube.com/embed/");
  }
  if (url.includes("vimeo.com")) {
    const match = url.match(/vimeo\.com\/(\d+)/);
    if (match) {
      newUrl = `https://player.vimeo.com/video/${match[1]}`;
    }
    if (!newUrl.includes("autoplay=1")) {
      newUrl += (newUrl.includes("?") ? "&" : "?") + "autoplay=1";
    }
  } else if (newUrl.includes("youtube.com/embed/")) {
    if (!newUrl.includes("autoplay=1")) {
      newUrl += (newUrl.includes("?") ? "&" : "?") + "autoplay=1";
    }
  }
  return newUrl;
};

const SnippetVideoCard = (props: Props_SnippetVideoCard) => {
  const { heading, repeatable_cards } = props;
  const [playing, setPlaying] = useState(
    Array(repeatable_cards.length).fill(false)
  );

  const headingLevel =
    (heading.level?.value as "h1" | "h2" | "h3" | "h4" | "h5" | "h6") || "h2";
  const headingAlignment =
    (heading.alignment?.value as "start" | "center" | "end") || undefined;
  const headingVisibility =
    typeof heading.visibility?.value === "boolean"
      ? heading.visibility.value
      : heading.visibility?.value === "true";

  const handlePlay = (idx: number) => {
    setPlaying((prev) => prev.map((val, i) => (i === idx ? true : val)));
  };

  return (
    <section className="snippet-video-card">
      <div className="snippet-video-card__container">
        <SharedHeading
          className="snippet-video-card__heading"
          level={headingLevel}
          alignment={headingAlignment}
          visibility={headingVisibility}
        >
          {heading.text?.value || ""}
        </SharedHeading>
        <div className="snippet-video-card__cards">
          {repeatable_cards.map((card, idx) => {
            const hasVideo = Boolean(card.videoSrc?.value);
            const thumbnailSrc = card.image?.value || getYouTubeThumbnail(card.videoSrc?.value || "");
            const showVideo = hasVideo && playing[idx];
            const classContentAlignment = `snippet-video-card__content--${card.contentAlignment?.value || "start"}`;
            return (
              <div className="snippet-video-card__card" key={idx}>
                <div className="snippet-video-card__media">
                  {thumbnailSrc && !showVideo && (
                    <figure className="snippet-video-card__thumbnail-container">
                      <img
                        src={thumbnailSrc}
                        alt={card.imageAlt?.value || ""}
                        className="snippet-video-card__thumbnail"
                        loading="lazy"
                        onError={(event) => {
                          const img = event.currentTarget;
                          if (img.src.includes("maxresdefault")) {
                            img.src = img.src.replace("maxresdefault", "hq720");
                          } else if (img.src.includes("hq720")) {
                            img.src = img.src.replace("hq720", "hqdefault");
                          }
                        }}
                      />
                      {hasVideo && (
                        <button
                          className="snippet-video-card__play-button attrax-button-play-white"
                          aria-label="Play video"
                          onClick={() => handlePlay(idx)}
                          type="button"
                        />
                      )}
                    </figure>
                  )}
                  {showVideo && card.videoSrc?.value && (
                    <iframe
                      className="snippet-video-card__video"
                      width="560"
                      height="315"
                      src={processVideoUrl(card.videoSrc.value)}
                      title="Embedded video player"
                      loading="lazy"
                      allow="autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      referrerPolicy="strict-origin-when-cross-origin"
                      allowFullScreen={true}
                    ></iframe>
                  )}
                </div>
                <div className={`snippet-video-card__content ${classContentAlignment}`}>
                  <SharedHeading
                    level="h3"
                    className="snippet-video-card__card-heading"
                  >
                    {card.cardHeading?.value || ""}
                  </SharedHeading>
                  {card.cardContent?.value && (
                    <div className="snippet-video-card__card-text">
                      {CompileMarkdown(card.cardContent.value)}
                    </div>
                  )}
                  <SnippetLink
                    url={card.linkHref?.value || ""}
                    linkStyles={card.linkStyle?.value}
                    openInNewTab={card.linkTarget?.value === true}
                    rel={
                      card.linkTarget?.value === true
                        ? "noopener noreferrer"
                        : undefined
                    }
                    className="snippet-video-card__card-link"
                  >
                    {card.linkText?.value}
                  </SnippetLink>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default SnippetVideoCard;